// Helpers shared by the convention pages. The rules mirror the Android app so
// both clients read and write the same tree the same way:
//   object/Contract.java (ordering) and ConventionDetailActivity.isValid()
//   (contract naming), ConventionActivity.isExist() (convention ID).

const DEGREES = { C: 1, D: 2, H: 3, S: 4, N: 5 };
const CONVENTION_ID_PATTERN = /^[a-z0-9]+$/;

export function normalizeContractName(value) {
  return (value || "").toUpperCase().replace(/\s+/g, "");
}

// PASS/X/XX first, then rank 1C..7NT (1C, 1D, 1H, 1S, 1NT, 2C, ...).
export function contractRank(value) {
  const name = normalizeContractName(value);
  if (name === "PASS" || name === "P") return 0;
  if (name === "X") return 1;
  if (name === "XX") return 2;
  const level = parseInt(name.charAt(0), 10);
  const degree = DEGREES[name.charAt(1)] || 0;
  return level * 10 + degree;
}

export function compareContracts(a, b) {
  return contractRank(a.name) - contractRank(b.name);
}

// Returns { valid, name?, error? }. Safe to call on empty input.
export function validateContractName(value) {
  const trimmed = (value || "").trim();
  if (!trimmed) return { valid: false, error: "Required!" };

  const name = normalizeContractName(trimmed);

  if (/^[1-7]/.test(name)) {
    const rest = name.slice(1);
    if (["C", "D", "H", "S", "NT"].indexOf(rest) >= 0) {
      return { valid: true, name: name };
    }
    return { valid: false, error: "Must following with C/D/H/S/NT" };
  }

  if (name.indexOf("P") === 0) {
    if (name === "P" || name === "PASS") return { valid: true, name: name };
    return { valid: false, error: "Do you mean Pass?" };
  }

  if (name.indexOf("X") === 0) {
    if (name === "X" || name === "XX") return { valid: true, name: name };
    return { valid: false, error: "Do you mean XX?" };
  }

  return { valid: false, error: "Must start with 1-7 or Pass, X, XX" };
}

export function normalizeConventionId(value) {
  return (value || "").trim().toLowerCase();
}

// Returns { valid, id?, error? } using the same messages as the Android app.
export function validateConventionId(value) {
  const id = normalizeConventionId(value);
  if (!id) return { valid: false, error: "Required" };
  if (!CONVENTION_ID_PATTERN.test(id)) {
    return { valid: false, error: "Must not contain special character" };
  }
  return { valid: true, id: id };
}

// `owner` in users/{uid}/conventions/{conId} counts as admin too.
export function roleIsAdmin(role) {
  return role === "owner" || role === "admin";
}

// The follower map is the source of truth for permission checks; the mirror in
// users/{uid}/conventions is only used for the directory list.
export function isConventionAdmin(convention, uid) {
  if (!convention || !uid) return false;
  if (convention.owner === uid) return true;
  return !!(convention.follower && convention.follower[uid] === "admin");
}
